/**
 * CustomImage - Image handling for TipTap editor
 * 
 * Provides:
 * - Custom image upload to the platform's file service
 * - Image paste handling with automatic upload
 * - Loading skeleton while uploading
 */

import { Image } from '@tiptap/extension-image';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { send_request } from '../../utils/Request';
import config from '../../config';

/**
 * Uploads an image file to the file service
 * @param {File} file - The image file to upload
 * @param {string} projectId - The project ID
 * @returns {Promise<string|null>} - The URL of the uploaded image, or null on failure
 */
export const uploadImage = async (file, projectId) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('configId', 'fileupload');
  formData.append('type', 'upload');
  formData.append('projectId', projectId);
  formData.append('projectSessionId', '');
  formData.append('formSessionId', '');

  try {
    const json = await send_request(
      'project-service/project/file-library/create',
      formData,
      '',
      'POST',
      { 'content-type': 'multipart/form-data' }
    );

    if (json && json.data) {
      return `${config.FILE_SERVICE.QUERY_URL}/${json.data.id}`;
    }
  } catch (error) {
    console.error('Error uploading image:', error);
  }

  return null;
};

/**
 * Handles image paste events
 * @param {Editor} editor - The TipTap editor instance
 * @param {string} projectId - The project ID for uploads
 * @returns {Function} - Event handler for paste events
 */
export const handleImagePaste = (editor, projectId) => {
  return async (view, event) => {
    const items = event.clipboardData?.items;
    if (!items) return false;

    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        event.preventDefault();
        
        const file = item.getAsFile();
        if (!file) continue;

        // Insert loading placeholder
        const uniqueId = `loading-img-${Date.now()}`;
        editor.chain().focus().insertContent(`<div id="${uniqueId}" class="image-loading-skeleton"></div>`).run();

        // Upload the image
        const url = await uploadImage(file, projectId);

        if (url) {
          // Replace loading placeholder with actual image
          const { state } = editor;
          const { doc } = state;
          let foundPos = null;

          doc.descendants((node, pos) => {
            if (node.type.name === 'paragraph' || node.type.name === 'doc') {
              const text = node.textContent;
              if (text.includes(uniqueId)) {
                foundPos = pos;
                return false;
              }
            }
            return true;
          });

          // Insert image at current position if placeholder not found
          editor.chain().focus().setImage({ src: url, alt: '' }).run();
        }

        return true;
      }
    }

    return false;
  };
};

/**
 * Extended Image extension with custom upload handling
 */
export const CustomImageExtension = Image.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      projectId: '',
      onUpload: null,
    };
  },

  addProseMirrorPlugins() {
    const plugins = this.parent?.() || [];
    const { projectId, onUpload } = this.options;

    // Add paste handling plugin
    plugins.push(
      new Plugin({
        key: new PluginKey('customImagePaste'),
        props: {
          handlePaste: (view, event) => {
            const items = event.clipboardData?.items;
            if (!items) return false;

            for (const item of items) {
              if (item.kind === 'file' && item.type.startsWith('image/')) {
                event.preventDefault();

                const file = item.getAsFile();
                if (!file) continue;

                // Use custom upload handler if provided
                if (onUpload) {
                  onUpload(file, this.editor);
                  return true;
                }

                // Default upload behavior
                (async () => {
                  const url = await uploadImage(file, projectId);
                  if (url) {
                    this.editor.chain().focus().setImage({ src: url, alt: '' }).run();
                  }
                })();

                return true;
              }
            }

            return false;
          },

          handleDrop: (view, event, slice, moved) => {
            if (moved) return false;

            const files = event.dataTransfer?.files;
            if (!files || files.length === 0) return false;

            for (const file of files) {
              if (file.type.startsWith('image/')) {
                event.preventDefault();

                // Use custom upload handler if provided
                if (onUpload) {
                  onUpload(file, this.editor);
                  return true;
                }

                // Default upload behavior
                (async () => {
                  const url = await uploadImage(file, projectId);
                  if (url) {
                    this.editor.chain().focus().setImage({ src: url, alt: '' }).run();
                  }
                })();

                return true;
              }
            }

            return false;
          },
        },
      })
    );

    return plugins;
  },
});

export default CustomImageExtension;
